import React, { useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { Route, selectRoute } from "src/config/routes";
import { CoownerAssemblyItem } from "./item/CoownerAssemblyItem";
import { useParams } from "react-router";
import { ASSEMBLY_STATE } from "src/stores/assemblies/AssemblyStore";

interface Params {
    realestateid: string;
    assemblyid: string;
}

const CoownerAssemblyDetailsBase = () => {
    const { t } = useTranslation();
    const { navStore, assemblyStore, authStore } = useContext(RootStoreContext);
    const { realestateid, assemblyid } = useParams<Params>();

    useEffect(() => {
        async function initializeAssemblyData() {
            await assemblyStore.loadCurrentAssembly(assemblyid);
        }
        assemblyStore.startIsAssemblyAssignedToRealestateSubscription(realestateid, assemblyid);
        initializeAssemblyData();
        assemblyStore.startSubscriptionHasAssemblyLiveModeChanged(assemblyid);

        // before demounting this component
        return () => {
            assemblyStore.stopIsAssemblyAssignedToRealestateSubscription();
            assemblyStore.stopSubscriptionHasAssemblyLiveModeChanged();
        };

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        navStore.setTitle(assemblyStore.currentAssembly?.title || "");
        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);
        navStore.setBackbutton(
            selectRoute(Route.coownerAssemblies, authStore.user?.role, { realestateid: realestateid }),
            t("screens.assemblies.sidebar_title").toString()
        );
    }, [
        navStore,
        assemblyStore.currentAssembly?.title,
        t,
        authStore.user?.role,
        realestateid,
        assemblyStore.isAssemblyAssignedToRealestate
    ]);

    if (
        assemblyStore.assemblyIsPreparing ||
        assemblyStore.currentAssembly?.state === ASSEMBLY_STATE.PREPARING ||
        (assemblyStore.hasCurrentAssemblyLoaded && !assemblyStore.isAssemblyAssignedToRealestate)
    ) {
        return (
            <UI.Container className="pt-4">
                <UI.Row>
                    <UI.Title>{t("screens.realestate.assembly.not_assigned_to_realestate")}</UI.Title>
                </UI.Row>
            </UI.Container>
        );
    }

    return (
        <UI.Container>
            {assemblyStore.hasCurrentAssemblyLoaded ? (
                <CoownerAssemblyItem realestateid={realestateid} />
            ) : (
                <UI.RotatingSpinner noLogo />
            )}
        </UI.Container>
    );
};

export const CoownerAssemblyDetails = Sentry.withProfiler(observer(CoownerAssemblyDetailsBase));
